import { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, updateProfile } from "firebase/auth";
import { useNavigate, useLocation } from 'react-router-dom';

function App() {
  const [dailyQuestionsCount, setDailyQuestionsCount] = useState(0);
  const [page, setPage] = useState("login");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [tier, setTier] = useState("Higher");
  const [examBoard, setExamBoard] = useState("AQA");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [currentQ, setCurrentQ] = useState(1);
  const [marksScored, setMarksScored] = useState(0);
  const [msVisible, setMsVisible] = useState(false);
  const [markSelected, setMarkSelected] = useState(false);
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashTab, setDashTab] = useState("overview");
  const [selectedMarks, setSelectedMarks] = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [topicScores, setTopicScores] = useState({});
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [authError, setAuthError] = useState("");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Sync URL with page state
  useEffect(() => {
    const path = location.pathname;
    if (path === '/login' || path === '/') {
      if (user) navigate('/setup');
      else setPage('login');
    } else if (path === '/setup' || path === '/new-paper') {
      if (!user) navigate('/login');
      else setPage('setup');
    } else if (path === '/quiz' || path === '/questions') {
      if (!user) navigate('/login');
      else setPage('quiz');
    } else if (path === '/dashboard') {
      if (!user) navigate('/login');
      else setPage('dashboard');
    } 
  }, [location, user, navigate]);

  // Update URL when page changes
  useEffect(() => {
    if (page === 'login') navigate('/login', { replace: true });
    else if (page === 'setup') navigate('/setup', { replace: true });
    else if (page === 'quiz') navigate('/questions', { replace: true });
    else if (page === 'dashboard') navigate('/dashboard', { replace: true });
  }, [page, navigate]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setAuthLoading(false);
      if (u) {
        setPage("setup");
        try {
          const userRef = doc(db, "users", u.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.topicScores) setTopicScores(data.topicScores);
            if (data.sessionHistory) setSessionHistory(data.sessionHistory);
          }
        } catch (err) {
          console.log("Error loading scores:", err);
        }
      } else {
        setPage("login");
      }
    });
    return unsub;
  }, []);

  const subjects = {
    Biology: { icon: "🧬", topics: ["Cell biology","Organisation","Bioenergetics","Infection & response","Homeostasis","Inheritance","Ecology"] },
    Chemistry: { icon: "⚗️", topics: ["Atomic structure","Bonding","Quantitative chemistry","Chemical changes","Energy changes","Rates of reaction","Organic chemistry","Electrolysis"] },
    Physics: { icon: "⚡", topics: ["Energy","Electricity","Particle model","Atomic structure","Forces","Waves","Magnetism"] },
    Maths: { icon: "📐", topics: ["Number","Algebra","Ratio","Geometry","Probability","Statistics","Trigonometry"] },
  };

  const examBoards = ["AQA", "Edexcel", "OCR"];

  async function handleAuth() {
    setAuthError("");
    if (!email || !password) { setAuthError("Please fill in all fields."); return; }
    if (authMode === "signup" && !name) { setAuthError("Please enter your name."); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    try {
      if (authMode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setAuthError(err.message.replace("Firebase: ", "").replace(/\(.*\)/, ""));
    }
  }

  async function handleLogout() {
    await signOut(auth);
    setSessionHistory([]);
    setTopicScores({});
  }

  function toggleSubject(name) {
    setSelectedSubjects(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
    setSelectedTopics([]);
  }

  function toggleTopic(topic) {
    setSelectedTopics(prev =>
      prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
    );
  }

  async function generateQuestion() {
  setLoading(true);
  setMsVisible(false);
  setMarkSelected(false);
  setSelectedMarks(null);
  setQuestion(null);

  // ========== ADD THIS ENTIRE SECTION ==========
  
  // Check daily limit
  if (user) {
    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const today = new Date().toISOString().split('T')[0]; // "2025-05-08"
        
        // Check if it's a new day
        if (userData.lastQuestionDate !== today) {
          // New day - reset counter
          await updateDoc(userRef, {
            questionsGeneratedToday: 1,
            lastQuestionDate: today
          });
        } else {
          // Same day - check limit
          const questionsToday = userData.questionsGeneratedToday || 0;
          
          if (questionsToday >= 20) {
            setQuestion({
              subject: "Daily Limit Reached",
              topic: "Come back tomorrow!",
              marks: 0,
              question: "You've generated 20 questions today. Come back tomorrow for more practice! 🎯",
              markscheme: ["Daily limit helps keep the service free for everyone"]
            });
            setLoading(false);
            return; // Stop here, don't generate question
          }
          
          // Increment counter
          await updateDoc(userRef, {
            questionsGeneratedToday: questionsToday + 1
          });
          
          setDailyQuestionsCount(questionsToday + 1); // Add this line
        }
      } else {
        // First time - create document
        await setDoc(userRef, {
          questionsGeneratedToday: 1,
          lastQuestionDate: new Date().toISOString().split('T')[0],
          topicScores: {},
          sessionHistory: []
        });
      }
    } catch (err) {
      console.error("Error checking daily limit:", err);
      // Continue anyway if there's an error
    }
  }
  
  // ========== END OF NEW SECTION ==========


    const subject = selectedSubjects[Math.floor(Math.random() * selectedSubjects.length)];
    const topicPool = selectedTopics.length > 0 ? selectedTopics : subjects[subject].topics;
    const topic = topicPool[Math.floor(Math.random() * topicPool.length)];
    const marks = [2, 3, 4, 5, 6][Math.floor(Math.random() * 5)];

    const timestamp = Date.now();
    const randomSeed = Math.random().toString(36).substring(7);

const prompt = `Generate a ${examBoard} GCSE ${subject} exam question.
Topic: ${topic}
Tier: ${tier}
Marks: ${marks}

Rules:
- Must be exam-style, GCSE appropriate
- Mark scheme must have exactly ${marks} points
${subject === 'Maths' ? '- Only round the FINAL answer to nearest integer\n- No rounding intermediate steps' : '- Focus on scientific knowledge, not calculations\n- Use accurate ${examBoard} terminology'}

Respond in JSON only, no other text:
{
  "subject": "${subject}",
  "topic": "${topic}",
  "marks": ${marks},
  "question": "question text here",
  "markscheme": ["mark point 1", "mark point 2"]
}`;

    try {
      const response = await fetch("https://paper-plus.onrender.com/api/question", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1500,
          messages: [{ role: "user", content: prompt }]
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} - ${response.statusText}`);
      }
      
      const reader = response.body.getReader();
const decoder = new TextDecoder();
let text = '';

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const lines = decoder.decode(value).split('\n');
  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;
    const data = line.slice(6);
    if (data === '[DONE]') break;
    try {
      const event = JSON.parse(data);
      if (event.type === 'content_block_delta' && event.delta?.text) {
        text += event.delta.text;
      }
    } catch {}
  }
}

if (!text) throw new Error('Invalid API response format - missing content');

// Remove markdown code blocks
let clean = text.replace(/```json|```/g, "").trim();

// Extract JSON from text (handles AI adding preambles)
const jsonMatch = clean.match(/\{[\s\S]*\}/);
if (!jsonMatch) {
  throw new Error('No valid JSON found in AI response');
}
clean = jsonMatch[0];

const parsed = JSON.parse(clean);
      
      // Validate parsed data
      if (!parsed.question || !parsed.markscheme || !Array.isArray(parsed.markscheme)) {
        throw new Error('Invalid question format - missing required fields');
      }
      
      if (parsed.markscheme.length === 0) {
        throw new Error('Mark scheme is empty');
      }
      
      setQuestion(parsed);
    } catch (err) {
      console.error('⚠️ Question generation error:', err);
      console.error('Error details:', {
        subject,
        topic,
        tier,
        examBoard,
        marks,
        timestamp: new Date().toISOString(),
        errorMessage: err.message,
        errorStack: err.stack
      });
      
      setQuestion({ 
        subject, 
        topic, 
        marks, 
        question: "⚠️ Unable to generate question. This error has been logged. Please click 'Next question' to try again, or visit our Contact Page if this persists.", 
        markscheme: ["Error occurred - please skip this question and try the next one"] 
      });
    }
    setLoading(false);
  }

  function startQuiz() {
    if (selectedSubjects.length === 0) return;
    setCurrentQ(1);
    setMarksScored(0);
    setPage("quiz");
    generateQuestion();
  }

  async function nextQuestion() {
    if (currentQ >= questionCount) {
      const newSession = {
        title: `${selectedSubjects.join(" + ")} — ${tier}`,
        sub: `${examBoard} · ${questionCount} questions`,
        score: marksScored,
        date: "Today"
      };
      setSessionHistory(prev => [newSession, ...prev.slice(0, 9)]);
      
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          const existingHistory = userDoc.exists() ? (userDoc.data().sessionHistory || []) : [];
          await setDoc(userRef, {
            sessionHistory: [newSession, ...existingHistory.slice(0, 9)]
          }, { merge: true });
        } catch (err) {
          console.log("Error saving session:", err);
        }
      }
      setPage("dashboard");
      return;
    }
    setCurrentQ(q => q + 1);
    generateQuestion();
  }

  async function handleMarkSelect(val) {
    if (markSelected) return;
    setSelectedMarks(val);
    setMarksScored(s => s + val);
    setMarkSelected(true);
    if (question) {
      const newTopicScores = { ...topicScores };
      const key = question.topic;
      const existing = newTopicScores[key] || { scored: 0, total: 0 };
      newTopicScores[key] = { scored: existing.scored + val, total: existing.total + question.marks };
      setTopicScores(newTopicScores);

      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const existingTopics = userDoc.data().topicScores || {};
            const existingTopic = existingTopics[key] || { scored: 0, total: 0 };
            await updateDoc(userRef, {
              [`topicScores.${key}`]: {
                scored: existingTopic.scored + val,
                total: existingTopic.total + question.marks
              }
            });
          } else {
            await setDoc(userRef, {
              topicScores: {
                [key]: { scored: val, total: question.marks }
              },
              sessionHistory: []
            });
          }
        } catch (err) {
          console.log("Error saving score:", err);
        }
      }
    }
  }

  const styles = {
    app: { fontFamily: "system-ui, sans-serif", maxWidth: 480, margin: "0 auto", padding: "1rem", background: "#f8f8f6", minHeight: "100vh" },
    nav: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "1.5rem" },
    logo: { fontSize: 22, fontWeight: 500 },
    logoSpan: { color: "#185FA5" },
    navBtn: { fontSize: 13, color: "#888", background: "none", border: "none", cursor: "pointer" },
    card: { background: "white", border: "0.5px solid #e0e0e0", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" },
    cardTitle: { fontSize: 16, fontWeight: 500, marginBottom: "0.5rem" },
    cardSub: { fontSize: 13, color: "#888", marginBottom: "1rem", lineHeight: 1.5 },
    subjectGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "0.5rem" },
    subjectBtn: (selected) => ({ background: selected ? "#E6F1FB" : "white", border: selected ? "2px solid #185FA5" : "0.5px solid #e0e0e0", borderRadius: 8, padding: 12, cursor: "pointer", textAlign: "left", transition: "all 0.15s" }),
    subjectIcon: { fontSize: 20, display: "block", marginBottom: 6 },
    subjectName: { fontSize: 14, fontWeight: 500, color: "#1a1a1a" },
    tierToggle: { display: "flex", background: "#f0f0ee", borderRadius: 8, padding: 3, gap: 3 },
    tierBtn: (active) => ({ flex: 1, padding: 8, border: active ? "0.5px solid #e0e0e0" : "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500, background: active ? "white" : "none", color: active ? "#1a1a1a" : "#888", transition: "all 0.15s" }),
    topicList: { display: "flex", flexWrap: "wrap", gap: 6 },
    topicTag: (selected) => ({ padding: "5px 10px", borderRadius: 999, border: selected ? "0.5px solid #185FA5" : "0.5px solid #e0e0e0", fontSize: 12, cursor: "pointer", background: selected ? "#E6F1FB" : "white", color: selected ? "#0C447C" : "#888", transition: "all 0.15s" }),
    primaryBtn: (disabled) => ({ width: "100%", padding: 12, background: disabled ? "#e0e0e0" : "#185FA5", color: disabled ? "#aaa" : "white", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer" }),
    progressWrap: { background: "#e0e0e0", borderRadius: 999, height: 4, margin: "0.5rem 0 1rem" },
    progressBar: (pct) => ({ background: "#185FA5", height: 4, borderRadius: 999, width: `${pct}%`, transition: "width 0.3s" }),
    qMeta: { display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap" },
    badge: (bg, color) => ({ fontSize: 11, padding: "3px 8px", borderRadius: 999, fontWeight: 500, background: bg, color }),
    qText: { fontSize: 15, lineHeight: 1.7, marginBottom: "1.25rem", color: "#1a1a1a" },
    btnRow: { display: "flex", gap: 8 },
    secBtn: { flex: 1, padding: 10, border: "0.5px solid #ccc", borderRadius: 8, background: "none", fontSize: 13, cursor: "pointer", color: "#1a1a1a" },
    msSection: { borderTop: "0.5px solid #e0e0e0", paddingTop: "1rem", marginTop: "1rem" },
    msTitle: { fontSize: 12, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: "0.75rem" },
    msPoint: { display: "flex", gap: 8, marginBottom: 6, fontSize: 14, lineHeight: 1.5 },
    msTick: { color: "#1D9E75", fontWeight: 500, flexShrink: 0 },
    marksRow: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: "1rem", paddingTop: "1rem", borderTop: "0.5px solid #e0e0e0" },
    marksLabel: { fontSize: 13, color: "#888" },
    markBubble: (selected) => ({ width: 32, height: 32, borderRadius: "50%", border: selected ? "none" : "0.5px solid #e0e0e0", background: selected ? "#185FA5" : "white", fontSize: 13, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: selected ? "white" : "#1a1a1a" }),
    dashGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "1rem" },
    metric: { background: "#f0f0ee", borderRadius: 8, padding: "1rem" },
    metricLabel: { fontSize: 12, color: "#888", marginBottom: 4 },
    metricValue: { fontSize: 24, fontWeight: 500, color: "#1a1a1a" },
    metricSub: { fontSize: 11, color: "#888", marginTop: 2 },
    navTabs: { display: "flex", background: "#f0f0ee", borderRadius: 8, padding: 3, gap: 3, marginBottom: "1.5rem" },
    navTab: (active) => ({ flex: 1, padding: 8, border: active ? "0.5px solid #e0e0e0" : "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500, background: active ? "white" : "none", color: active ? "#1a1a1a" : "#888", textAlign: "center" }),
    barRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
    barLabel: { fontSize: 12, color: "#888", width: 90, flexShrink: 0 },
    barTrack: { flex: 1, background: "#f0f0ee", borderRadius: 999, height: 8 },
    barFill: (pct, color) => ({ height: 8, borderRadius: 999, background: color || "#185FA5", width: `${pct}%` }),
    barPct: { fontSize: 12, color: "#888", width: 32, textAlign: "right" },
    examBoardToggle: { display: "flex", background: "#f0f0ee", borderRadius: 8, padding: 3, gap: 3 },
    examBoardBtn: (active) => ({ flex: 1, padding: 8, border: active ? "0.5px solid #e0e0e0" : "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500, background: active ? "white" : "none", color: active ? "#1a1a1a" : "#888", transition: "all 0.15s" }),
    input: { width: "100%", padding: "10px 12px", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 14, marginBottom: 10, boxSizing: "border-box", background: "white", color: "#1a1a1a" },
    authToggle: { fontSize: 13, color: "#888", textAlign: "center", marginTop: "1rem" },
    authLink: { color: "#185FA5", cursor: "pointer", marginLeft: 4 },
    errorMsg: { fontSize: 13, color: "#E24B4A", marginBottom: 10, textAlign: "center" },
    contactBtn: { fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" },
  };

  if (authLoading) return (
    <div style={{ ...styles.app, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 14, color: "#888" }}>Loading...</div>
    </div>
  );

 

  if (page === "login") return (
    <div style={styles.app}>
      <div style={{ textAlign: "center", padding: "2rem 0 1.5rem" }}>
        <div style={styles.logo}>Paper<span style={styles.logoSpan}>Plus</span></div>
        <div style={{ fontSize: 14, color: "#888", marginTop: 6 }}>GCSE exam practice, powered by AI</div>
      </div>
      <div style={styles.card}>
        <div style={styles.navTabs}>
          <button style={styles.navTab(authMode === "login")} onClick={() => { setAuthMode("login"); setAuthError(""); }}>Log in</button>
          <button style={styles.navTab(authMode === "signup")} onClick={() => { setAuthMode("signup"); setAuthError(""); }}>Sign up</button>
        </div>
        {authMode === "signup" && (
          <input style={styles.input} placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
        )}
        <input style={styles.input} placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={styles.input} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
        {authError && <div style={styles.errorMsg}>{authError}</div>}
        <button style={styles.primaryBtn(false)} onClick={handleAuth}>
          {authMode === "login" ? "Log in" : "Create account"}
        </button>
        <div style={styles.authToggle}>
          {authMode === "login" ? (
            <>Don't have an account?<span style={styles.authLink} onClick={() => { setAuthMode("signup"); setAuthError(""); }}>Sign up</span></>
          ) : (
            <>Already have an account?<span style={styles.authLink} onClick={() => { setAuthMode("login"); setAuthError(""); }}>Log in</span></>
          )}
        </div>
        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
          <a href="/contact.html" style={styles.contactBtn}>Contact Us</a>
        </div>
        <div style={{ textAlign: "center", marginTop: "1rem", fontSize: 11, color: "#888" }}>
          <a href="/privacy.html" target="_blank" style={{ color: "#185FA5", marginRight: 12, textDecoration: "none" }}>Privacy</a>
          <a href="/terms.html" target="_blank" style={{ color: "#185FA5", textDecoration: "none" }}>Terms</a>
        </div>
      </div>
    </div>
  );

  if (page === "setup") return (
    <div style={styles.app}>
      <div style={styles.nav}>
        <div style={styles.logo}>Paper<span style={styles.logoSpan}>Plus</span></div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <a href="/contact.html" style={styles.navBtn}>Contact</a>
          <button style={styles.navBtn} onClick={() => setPage("dashboard")}>Dashboard</button>
          <button style={styles.navBtn} onClick={handleLogout}>Log out</button>
        </div>
      </div>

      {user && <div style={{ fontSize: 13, color: "#888", marginBottom: "1rem" }}>Welcome back, {user.displayName || user.email} 👋</div>}

      <div style={styles.card}>
        <div style={styles.cardTitle}>Exam board</div>
        <div style={styles.cardSub}>Select your exam board.</div>
        <div style={styles.examBoardToggle}>
          {examBoards.map(board => (
            <button key={board} style={styles.examBoardBtn(examBoard === board)} onClick={() => setExamBoard(board)}>{board}</button>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Choose your subjects</div>
        <div style={styles.cardSub}>Select one or more subjects for your paper. All subjects are free!</div>
        <div style={styles.subjectGrid}>
          {Object.entries(subjects).map(([name, data]) => (
            <button key={name} style={styles.subjectBtn(selectedSubjects.includes(name))} onClick={() => toggleSubject(name)}>
              <span style={styles.subjectIcon}>{data.icon}</span>
              <div style={styles.subjectName}>{name}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Tier</div>
        <div style={styles.tierToggle}>
          {["Foundation", "Higher"].map(t => (
            <button key={t} style={styles.tierBtn(tier === t)} onClick={() => setTier(t)}>{t}</button>
          ))}
        </div>
      </div>

      {selectedSubjects.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Topics</div>
          <div style={styles.cardSub}>Filter by topic or leave all unselected for a mixed paper.</div>
          <div style={styles.topicList}>
            {selectedSubjects.flatMap(s => subjects[s].topics).filter((v, i, a) => a.indexOf(v) === i).map(topic => (
              <span key={topic} style={styles.topicTag(selectedTopics.includes(topic))} onClick={() => toggleTopic(topic)}>{topic}</span>
            ))}
          </div>
        </div>
      )}

      <div style={styles.card}>
  <div style={styles.cardTitle}>Number of questions</div>
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: "0.5rem" }}>
    <input
      type="number"
      min="1"
      max="20"  
      value={questionCount}
      onChange={e => setQuestionCount(Math.min(20, Number(e.target.value)))}  
      style={{ width: 80, padding: "8px 12px", border: "0.5px solid #e0e0e0", borderRadius: 8, fontSize: 15, fontWeight: 500 }}
    />
    <span style={{ fontSize: 13, color: "#888" }}>questions (max 20 per day)</span>  
  </div>
</div>

      <button style={styles.primaryBtn(selectedSubjects.length === 0)} onClick={startQuiz} disabled={selectedSubjects.length === 0}>Generate paper</button>
    </div>
  );

  if (page === "quiz") return (
    <div style={styles.app}>
      <div style={styles.nav}>
        <div style={styles.logo}>Paper<span style={styles.logoSpan}>Plus</span></div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <a href="/contact.html" style={styles.navBtn}>Contact</a>
          <button style={styles.navBtn} onClick={() => setPage("setup")}>Exit</button>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
  <span style={{ fontSize: 13, color: "#888" }}>Question {currentQ} of {questionCount}</span>
  <span style={{ fontSize: 12, color: "#888" }}>{marksScored} marks scored</span>
</div>
<div style={styles.progressWrap}>
  <div style={styles.progressBar((currentQ / questionCount) * 100)}></div>
</div>

{/* 👇 ADD THIS 👇 */}
{user && (
  <div style={{ fontSize: 11, color: "#888", textAlign: "center", marginBottom: "1rem" }}>
    Questions generated today: {dailyQuestionsCount}/20
  </div>
)}

      <div style={styles.card}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#888", fontSize: 14 }}>Generating question...</div>
        ) : question ? (
          <>
            <div style={styles.qMeta}>
              <span style={styles.badge("#E6F1FB", "#0C447C")}>{question.subject}</span>
              <span style={styles.badge("#EEEDFE", "#3C3489")}>{question.topic}</span>
              <span style={styles.badge("#EAF3DE", "#3B6D11")}>{examBoard}</span>
              <span style={styles.badge("#f0f0ee", "#888")}>{question.marks} marks</span>
            </div>
            <div style={styles.qText}>{question.question}</div>
            <div style={styles.btnRow}>
              <button style={styles.secBtn} onClick={() => setMsVisible(v => !v)}>{msVisible ? "Hide mark scheme" : "Show mark scheme"}</button>
              <button style={styles.secBtn} onClick={nextQuestion}>{currentQ >= questionCount ? "Finish" : "Next question"}</button>
            </div>
            {msVisible && (
              <div style={styles.msSection}>
                <div style={{ 
                  fontSize: 12, 
                  color: "#856404", 
                  background: "#FFF3CD", 
                  padding: "10px 12px", 
                  borderRadius: 6,
                  marginBottom: "1rem",
                  border: "1px solid #FFE69C",
                  lineHeight: 1.5
                }}>
                  <strong>⚠️ Accuracy Notice:</strong> This mark scheme is AI-generated and may contain occasional errors. Always verify answers with your textbook, teacher, or official exam board materials. Report errors via our <a href="/contact.html" style={{ color: "#856404", fontWeight: 500 }}>Contact Page</a>
                </div>
                <div style={styles.msTitle}>Mark scheme</div>
                {question.markscheme.map((point, i) => (
                  <div key={i} style={styles.msPoint}><span style={styles.msTick}>✓</span><span>{point}</span></div>
                ))}
                <div style={styles.marksRow}>
                  <span style={styles.marksLabel}>How many marks?</span>
                  {Array.from({ length: question.marks + 1 }, (_, i) => (
                    <div key={i} style={styles.markBubble(selectedMarks === i)} onClick={() => handleMarkSelect(i)}>{i}</div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );

  if (page === "dashboard") return (
    <div style={styles.app}>
      <div style={styles.nav}>
        <div style={styles.logo}>Paper<span style={styles.logoSpan}>Plus</span></div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <a href="/contact.html" style={styles.navBtn}>Contact</a>
          <button style={styles.navBtn} onClick={() => setPage("setup")}>New paper</button>
          <button style={styles.navBtn} onClick={handleLogout}>Log out</button>
        </div>
      </div>
      <div style={styles.navTabs}>
        {["overview", "topics", "history"].map(t => (
          <button key={t} style={styles.navTab(dashTab === t)} onClick={() => setDashTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {dashTab === "overview" && (
        <>
          <div style={styles.dashGrid}>
            <div style={styles.metric}><div style={styles.metricLabel}>Total marks</div><div style={styles.metricValue}>{marksScored}</div><div style={styles.metricSub}>this session</div></div>
            <div style={styles.metric}><div style={styles.metricLabel}>Questions done</div><div style={styles.metricValue}>{currentQ - 1}</div><div style={styles.metricSub}>this session</div></div>
          </div>
          {Object.keys(topicScores).length > 0 ? (
            <div style={styles.card}>
              <div style={{ ...styles.cardTitle, marginBottom: "1rem" }}>Your scores by topic</div>
              {Object.entries(topicScores).map(([topic, { scored, total }]) => {
                const pct = Math.round((scored / total) * 100);
                const color = pct > 70 ? "#185FA5" : pct > 50 ? "#EF9F27" : "#E24B4A";
                return (
                  <div key={topic} style={styles.barRow}>
                    <span style={styles.barLabel}>{topic}</span>
                    <div style={styles.barTrack}><div style={styles.barFill(pct, color)}></div></div>
                    <span style={styles.barPct}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.card}>
              <div style={{ fontSize: 14, color: "#888", textAlign: "center", padding: "1rem 0" }}>Complete a session to see your scores here.</div>
            </div>
          )}
        </>
      )}

      {dashTab === "topics" && (
        <div style={styles.card}>
          {Object.keys(topicScores).length > 0 ? (
            <>
              <div style={{ ...styles.cardTitle, marginBottom: "1rem" }}>Topic breakdown</div>
              {Object.entries(topicScores).map(([topic, { scored, total }]) => {
                const pct = Math.round((scored / total) * 100);
                const color = pct > 70 ? "#185FA5" : pct > 50 ? "#EF9F27" : "#E24B4A";
                return (
                  <div key={topic} style={styles.barRow}>
                    <span style={styles.barLabel}>{topic}</span>
                    <div style={styles.barTrack}><div style={styles.barFill(pct, color)}></div></div>
                    <span style={styles.barPct}>{pct}%</span>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ fontSize: 14, color: "#888", textAlign: "center", padding: "1rem 0" }}>Complete a session to see topic scores.</div>
          )}
        </div>
      )}

      {dashTab === "history" && (
        <div style={styles.card}>
          <div style={{ ...styles.cardTitle, marginBottom: "1rem" }}>Recent sessions</div>
          {sessionHistory.length === 0 ? (
            <div style={{ fontSize: 14, color: "#888", textAlign: "center", padding: "1rem 0" }}>No sessions yet. Complete a paper to see your history.</div>
          ) : (
            sessionHistory.map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < sessionHistory.length - 1 ? "0.5px solid #e0e0e0" : "none" }}>
                <div><div style={{ fontSize: 14, fontWeight: 500 }}>{s.title}</div><div style={{ fontSize: 12, color: "#888" }}>{s.sub}</div></div>
                <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 500, color: "#185FA5" }}>{s.score} marks</div><div style={{ fontSize: 12, color: "#888" }}>{s.date}</div></div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default App;